import { FunctionComponent, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Page, Loader, Div } from '@gaiads/telia-react-component-library';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InvoicingAccountOptionResponseContents } from '@cdaas/api-clients/dist/asset-user-openapi';

import UserGroupsList from './UserGroupsList';
import { getUserGroups, showGrowlNotification } from '../../../store/actions';
import OrganizationSetupHeader from '../OrganizationSetupHeader/OrganizationSetupHeader';
import { RootState } from '../../../store/reducers';
import { UserGroup } from '../../../types';
import { devLogger, getInvoicingAccounts } from '../../../utils';

const UserGroups: FunctionComponent = () => {
  const userGroups: UserGroup[] = useSelector((state: RootState) => state.userGroups.userGroups, shallowEqual);
  const userGroupsLoading: boolean = useSelector((state: RootState) => state.userGroups.loading);
  const isAuthenticated: boolean = useSelector((state: RootState) => state.authentication.authenticated);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('onboarding');

  const [organizationInvoicingAccounts, setOrganizationInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [organizationInvoicingAccountsLoading, setOrganizationInvoicingAccountsLoading] = useState<boolean>(false);

  const loading = userGroupsLoading || organizationInvoicingAccountsLoading;

  useEffect(() => {
    async function fetchInvoicingAccountsAndUserGroups() {
      if (isAuthenticated) {
        setOrganizationInvoicingAccountsLoading(true);
        const invoicingAccounts = await getInvoicingAccounts();
        setOrganizationInvoicingAccounts(invoicingAccounts || []);
        setOrganizationInvoicingAccountsLoading(false);
        // GETing invoicing accounts from asset service makes asset service update invoicing accounts information
        // (e.g. has some invoicing account been removed) from Telia finance.
        // GETing invoicing accounts before GETing usergroups make sure usergroups invoicing accounts have up-to-date info in their active=true|false
        dispatch(getUserGroups());
      }
    }
    fetchInvoicingAccountsAndUserGroups();
  }, [isAuthenticated, dispatch]);

  const validateAndSaveUserGroups = (userGroups: unknown) => {
    devLogger('Validating user groups', userGroups);
    const hasNoEmptyGroups = (userGroups as UserGroup[]).every(group => group.name && group.default_user_roles.length > 0);
    const areAllGroupsSaved = (userGroups as UserGroup[]).every(group => group.modified !== true);

    if (hasNoEmptyGroups && areAllGroupsSaved) {
      history.push('/onboarding/users');
    } else {
      dispatch(showGrowlNotification(t('userGroupsNotOkNotification'), 'error'));
    }
  };

  return (
    <Page>
      <OrganizationSetupHeader onboardingCurrentStepIndex={0} inputData={userGroups} validateAndSaveOnboardingStepData={validateAndSaveUserGroups} />

      {loading ? (
        <Div alignTextToCenter margin={{ top: 'xlg' }}>
          <Loader />
        </Div>
      ) : (
        <UserGroupsList organizationInvoicingAccounts={organizationInvoicingAccounts} />
      )}
    </Page>
  );
};

export default UserGroups;
